<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :collectionFilters="getCollectionFilters"
      :columns="columns[panelType]"
      :gettingStartedItemType="gettingStartedItemType"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :handleUnselectLockedItem="handleUnselectLockedItem"
      :handleUnselectMultipleLockedItems="handleUnselectMultipleLockedItems"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isItemLocked="isItemLocked"
      :itemType="itemType"
      :onMountedAfterFetch="handleMountedAfterFetch"
      :parentOS="parentOS"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="selectionText"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :setSelectedModelId="setSelectedModelId"
      :showActionBar="true"
      :showBound="!inMainPanel"
      :showBoundInitially="isGroupOrSystemAside"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :storeModule="storeModule"
      :useDropDownFilterComponent="useDropDownFilterComponent"
      :viewport="viewport"
    />
    <AlertContainer>
      <Alert
        v-if="deletedPolicies.deletedIds.length > 0"
        :delay="7000"
        type="success"
        @hidden="handleDeleteAlertHidden('deletedIds')"
      >
        <span>
          {{ deletedPolicies.successMessage }}
        </span>
      </Alert>
      <Alert
        v-if="!show403error && deletedPolicies.failedTasks.length > 0"
        :delay="7000"
        type="error"
        @hidden="handleDeleteAlertHidden('failedTasks')"
      >
        <span>
          {{ deletedPolicies.errorMessage }}
        </span>
      </Alert>
      <Alert
        v-if="show403error"
        :delay="7000"
        type="error"
        @hidden="handle403AlertHidden"
      >
        <span>{{ errorMessageFor403 }}</span>
      </Alert>
    </AlertContainer>
  </div>
</template>

<script>
import {
  DirectoryGroupingConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  PolicyConsts,
  StoreModuleMap,
  osFamilyTypes,
} from 'client/util/Constants';
import { browserPolicyNames, ospPolicyNames } from 'client/util/Constants/OsPatchMgmtConstants';
import { mapActions, mapGetters } from 'vuex';
import Alert from 'client/components/Alert/Alert.vue';
import AlertContainer from 'client/components/Alert/AlertContainer.vue';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DeleteConfirmationModal from 'components/Modal/ModalComponents/DeleteConfirmationModal.vue';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'components/Modal/ModalEventBus';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import PolicyOSIcon from 'client/components/ItemTable/BodyComponents/PolicyOSIcon.vue';
import pluralize from 'pluralize';
import rbacUtils from 'client/util/rbacUtils';

const { ConfigurationItemType } = ItemType;
const configurationItem = new ConfigurationItemType();

const {
  groupAsidePanel,
  mainPanel,
  systemAsidePanel,
} = ItemTableConsts.panelType;

const { universal } = osFamilyTypes;

const { columnTypes } = ItemTableConsts;
const storeModule = StoreModuleMap[ItemTypeConsts.policy];

export default {
  name: 'PoliciesItemTable',

  components: {
    Alert,
    AlertContainer,
    ItemTable,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTablePropsMixin,
    ItemTableMethodsMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.policy,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
    recommendationSectionVisible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const columns = {};

    const defaultColumns = () => (
      [
        {
          bodyComponent: PolicyOSIcon,
          classNames: {
            body: [columnTypes.iconColumn],
          },
          headingText: 'Type',
          sortBy: 'template.osMetaFamily',
        },
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: 'name',
            subtitleField: 'template.displayName',
          },
          sortBy: 'name',
          headingText: 'Name',
        },
        {
          bodyComponent: 'RequirementsDetail',
          headingText: 'Requirements',
        },
      ]
    );

    const systemColumns = () => (
      [
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: 'name',
            subtitleField: 'template.displayName',
          },
          sortBy: 'name',
          headingText: 'Policy',
        },
      ]
    );

    columns[mainPanel] = defaultColumns();
    columns[groupAsidePanel] = defaultColumns();
    columns[systemAsidePanel] = systemColumns();

    return {
      columns,
      deletedPolicies: {
        deletedIds: [],
        errorMessage: '',
        failedTasks: [],
        successMessage: '',
      },
      errorMessageFor403: rbacUtils.getForbiddenMessage(),
      groupType: undefined,
      memberType: undefined,
      show403error: false,
      storeModule,
      ItemTypeConsts,
    };
  },

  computed: {
    ...mapGetters(storeModule, {
      getBoundItem: 'getBoundItem',
      getFilters: 'getFilters',
      selectedItems: 'getSelectedItems',
    }),

    getCollectionFilters() {
      const collectionFilters = [];

      if (this.isSystemAsidePanel() && this.parentOS) {
        collectionFilters.push({
          name: 'template.os_meta_family',
          operator: 'in',
          value: `${this.parentOS.toUpperCase()}|${universal.toUpperCase()}`,
        });
      }

      // Do not show OS/Browser Patch management instances in Policies main panel
      if (this.isMainPanel()) {
        collectionFilters.push(...ospPolicyNames.map(name => ({
          name: 'template.name',
          operator: 'ne',
          value: name,
        })));

        collectionFilters.push(...browserPolicyNames.map(name => ({
          name: 'template.name',
          operator: 'ne',
          value: name,
        })));
      }

      return collectionFilters;
    },

    isGroupOrSystemAside() {
      return this.panelType === ItemTableConsts.panelType.groupAsidePanel
        || this.panelType === ItemTableConsts.panelType.systemAsidePanel;
    },

    route() {
      return ItemTypeRoutes[this.itemType];
    },

    selectionText() {
      return {
        label: this.inMainPanel ? 'selected' : 'bound',
        plural: configurationItem.labelPlural,
        singular: configurationItem.label,
      };
    },
  },

  beforeCreate() {
    const { parentType } = this.$options.propsData;
    this.$options.methods.handleBeforeCreate(this.$options, storeModule);

    if (parentType === ItemTypeConsts.device) {
      this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
    }
  },

  created() {
    const { groupTypes, memberTypes } = DirectoryGroupingConsts;

    // Set the group type for use in ItemTableIndirectBindingMixin
    if (this.panelType === systemAsidePanel) {
      this.groupType = groupTypes.systemGroup;
      this.memberType = memberTypes.system;
    }
  },

  methods: {
    ...mapActions(storeModule, [
      'deleteItems',
      'setFilter',
    ]),

    // checks if osp filters are missing and applies them to the PolicyCollection module if needed
    applyMainPanelFilters() {
      const currentFilters = this.getFilters;
      const filtersToApply = ospPolicyNames.map(name => ({
        name: 'template.name',
        operator: 'ne',
        value: name,
      }));

      const filtersNeeded = filtersToApply.every(filter => !currentFilters
        .find(currentFilter => (
          currentFilter.name === filter.name
          && currentFilter.operator === filter.operator
          && currentFilter.value === filter.value
        )));

      if (filtersNeeded) {
        filtersToApply.forEach(filter => this.setFilter({ filter, reset: false }));
      }
    },

    checkForFdePolicy(selected) {
      const { FILE_VAULT_TEMPLATE_NAME, BITLOCKER_TEMPLATE_NAME } = PolicyConsts;
      const policies = selected.reduce((acc, id) => {
        const { template } = this.getBoundItem(id);
        if (template.name === FILE_VAULT_TEMPLATE_NAME) {
          acc.FileVault = true;
        }
        if (template.name === BITLOCKER_TEMPLATE_NAME) {
          acc.Bitlocker = true;
        }
        return acc;
      }, {});
      return Object.keys(policies);
    },

    deleteSelectedPolicies() {
      ModalEventBus.close();

      rbacUtils.dontUseNotifyUtil(() => {
        this.show403error = true;
      });

      this.deleteItems({
        ids: this.selectedItems,
        isOsPatchPolicy: false,
      })
        .then((results) => {
          const { deletedIds, failedTasks } = results;

          if (failedTasks.length > 0) {
            this.deletedPolicies.failedTasks.push(...failedTasks);
            this.deletedPolicies
              .errorMessage = `There was an error deleting the selected ${pluralize('Policy', this.selectedItems.length)}.`;
          } else {
            this.deletedPolicies.deletedIds.push(...deletedIds);
            this.deletedPolicies
              .successMessage = `${results.deletedIds.length} ${pluralize('Policy', results.deletedIds.length)} successfully deleted.`;
          }
        })
        .catch(() => {});
    },

    handle403AlertHidden() {
      this.show403error = false;
    },

    handleBulkDelete() {
      const fdePolicyType = this.checkForFdePolicy(this.selectedItems);
      const policyString = fdePolicyType.join(' and ');
      const fdeString = `Removing the ${policyString} ${pluralize('policy', fdePolicyType.length)} will suspend enforcement but will not remove full disk encryption.`;

      ModalEventBus.open({
        component: DeleteConfirmationModal,
        componentProps: {
          handleContinue: this.deleteSelectedPolicies,
          enableMainAction: true,
          bodyText: `Are you sure you would like to delete the selected ${pluralize('policy', this.selectedItems.length)}?`,
          headerText: `Delete ${pluralize('Policy', this.selectedItems.length)}`,
          additionalText: fdePolicyType.length > 0 ? fdeString : '',
        },
      });
    },

    /**
     * Reset the particular section of the deletedPolicies data member to an empty array when the
     * Alert is hidden.
     */
    handleDeleteAlertHidden(key) {
      this.deletedPolicies[key].splice(0);
    },

    handleMainAction() {
      PageNavigationUtil.setLocation(`${this.route}/choose`);
    },

    handleMountedAfterFetch() {
      // loads osp filters when loading the policies main panel from a devices aside hotlink
      // see: https://jumpcloud.atlassian.net/browse/TX-243
      if (this.isMainPanel()) {
        this.applyMainPanelFilters();
      }
    },

    isMainPanel() {
      return this.panelType === ItemTableConsts.panelType.mainPanel;
    },

    isSystemAsidePanel() {
      return this.panelType === ItemTableConsts.panelType.systemAsidePanel;
    },
  },
};
</script>
